import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Avatar, AvatarDetails, DefaultAvatar } from '../../atoms/icons/Avatar'
import { MessageIcon } from '../../atoms/icons/MessageIcon'
import { PersonIcon } from '../../atoms/icons/PersonIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const getInitials = (displayName: string): string => {
  const parts = displayName.split(' ').filter(Boolean)
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''
  return `${first}${last}`.toUpperCase()
}

export interface CareTeamMember {
  providerId?: string
  canMessage?: boolean
  displayName: string
  role: string
  avatarDetails?: AvatarDetails
  /** Marks this member as the logged-in provider */
  isSelf?: boolean
  /** Marks this member as external (e.g. referring provider) */
  isExternal?: boolean
}

export interface UnassignedRoles {
  roles: string[]
}

export interface CareTeamProgramCardProps {
  programName: string
  members: CareTeamMember[]
  unassigned?: UnassignedRoles
  testID?: string
  onMessageProvider?: (providerId: string) => void
}

const CardContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  borderRadius: 16,
  padding: theme.spacing['24px'],
  gap: theme.spacing['8px'],
}))

const MembersContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['8px'],
}))

const MemberRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
  alignItems: 'center',
}))

const AvatarCircle = styled.View<{ theme: ThemeType; bg?: string }>(({ theme, bg }) => ({
  width: theme.spacing['24px'],
  height: theme.spacing['24px'],
  borderRadius: 12,
  backgroundColor: bg ?? 'transparent',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
}))

const MessageButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

const SCALED_ICON_STYLE = { transform: [{ scale: 0.5 }] } as const

const RowContent = styled.View({
  flex: 1,
})

const MemberName: FunctionComponent<{
  displayName: string
  isSelf?: boolean
  isExternal?: boolean
  color: string
  index: number
}> = ({ displayName, isSelf, isExternal, color, index }) => {
  const { formatMessage } = useIntl()
  let suffix = ''
  if (isSelf)
    suffix = formatMessage({
      defaultMessage: '(yourself)',
      description: 'Suffix indicating the logged-in provider is this care team member',
    })
  else if (isExternal)
    suffix = formatMessage({
      defaultMessage: '(external)',
      description: 'Suffix indicating a care team member is an external provider',
    })

  return (
    <BodyText
      size={BodyTextSize.SMALL}
      color={color}
      /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
      text={suffix ? `${displayName} ${suffix}` : displayName}
      testID={tID(`CareTeamProgramCard-memberName-${index}`)}
    />
  )
}

const MemberRowItem: FunctionComponent<{
  member: CareTeamMember
  index: number
  colors: ThemeType['colors']
  testIdPrefix: string
  onMessageProvider?: (providerId: string) => void
}> = ({ member, index, colors, testIdPrefix, onMessageProvider }) => {
  const { formatMessage } = useIntl()
  const { providerId } = member

  return (
    <MemberRow testID={tID(`${testIdPrefix}-${index}`)}>
      {member.isExternal ? (
        <AvatarCircle bg={colors.backgroundElement}>
          <BodyText
            size={BodyTextSize.BADGE}
            color={colors.textSecondary}
            text={getInitials(member.displayName)}
            style={{ lineHeight: 12 }}
          />
        </AvatarCircle>
      ) : member.avatarDetails?.src ? (
        <AvatarCircle>
          <Avatar
            details={member.avatarDetails}
            size={24}
            accessibilityLabel={member.displayName}
            defaultAvatarFontSize={BodyTextSize.BADGE}
          />
        </AvatarCircle>
      ) : (
        <AvatarCircle>
          <DefaultAvatar displayName={member.displayName} size={24} fontSize={BodyTextSize.BADGE} />
        </AvatarCircle>
      )}
      <RowContent>
        <MemberName
          displayName={member.displayName}
          isSelf={member.isSelf}
          isExternal={member.isExternal}
          color={colors.textSecondary}
          index={index}
        />
        <BodyText
          size={BodyTextSize.CAPTION}
          color={colors.textSecondary}
          /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
          text={member.role}
          testID={tID(`CareTeamProgramCard-memberRole-${index}`)}
        />
      </RowContent>
      {member.canMessage && providerId && onMessageProvider && (
        <MessageButton
          testID={tID(`CareServices-message-${providerId}`)}
          accessibilityRole='button'
          accessibilityLabel={formatMessage(
            { defaultMessage: 'Message {providerName}', description: 'Message a current care provider' },
            { providerName: member.displayName },
          )}
          onPress={() => onMessageProvider(providerId)}
        >
          <MessageIcon size={24} />
        </MessageButton>
      )}
    </MemberRow>
  )
}

export interface CareTeamMembersListProps {
  members: CareTeamMember[]
  unassigned?: UnassignedRoles
  onMessageProvider?: (providerId: string) => void
}

export const CareTeamMembersList: FunctionComponent<CareTeamMembersListProps> = ({
  members,
  unassigned,
  onMessageProvider,
}) => {
  const { colors } = useTheme() as ThemeType
  const internalMembers = members.filter((m) => !m.isExternal)
  const externalMembers = members.filter((m) => m.isExternal)

  return (
    <MembersContainer>
      {internalMembers.map((member, index) => (
        <MemberRowItem
          key={`${member.displayName}-${index}`}
          member={member}
          index={index}
          colors={colors}
          testIdPrefix='CareTeamProgramCard-member'
          onMessageProvider={onMessageProvider}
        />
      ))}

      {!!unassigned?.roles.length && (
        <MemberRow testID={tID('CareTeamProgramCard-unassigned')}>
          <AvatarCircle bg={colors.components.badge.neutral.background}>
            <View style={SCALED_ICON_STYLE}>
              <PersonIcon fillColor={colors.iconDefault} />
            </View>
          </AvatarCircle>
          <RowContent>
            <BodyText
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='Unassigned ({count})'
                  description='Label showing the count of unassigned care team roles for a program'
                  values={{ count: unassigned.roles.length }}
                />
              }
              testID={tID('CareTeamProgramCard-unassignedLabel')}
            />
            {unassigned.roles.map((role, index) => (
              <BodyText
                key={`${role}-${index}`}
                size={BodyTextSize.CAPTION}
                color={colors.textSecondary}
                /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                text={role}
                testID={tID(`CareTeamProgramCard-unassignedRole-${index}`)}
              />
            ))}
          </RowContent>
        </MemberRow>
      )}

      {externalMembers.map((member, index) => (
        <MemberRowItem
          key={`${member.displayName}-${index}`}
          member={member}
          index={internalMembers.length + index}
          colors={colors}
          testIdPrefix='CareTeamProgramCard-external'
          onMessageProvider={onMessageProvider}
        />
      ))}
    </MembersContainer>
  )
}

export const CareTeamProgramCard: FunctionComponent<CareTeamProgramCardProps> = ({
  programName,
  members,
  unassigned,
  testID,
  onMessageProvider,
}) => {
  const { colors } = useTheme() as ThemeType

  return (
    <CardContainer testID={tID(testID ?? 'CareTeamProgramCard')}>
      <BodyText
        size={BodyTextSize.DEFAULT}
        color={colors.textPrimary}
        /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
        text={programName}
        testID={tID('CareTeamProgramCard-programName')}
      />
      <CareTeamMembersList members={members} unassigned={unassigned} onMessageProvider={onMessageProvider} />
    </CardContainer>
  )
}
